import React, { ReactNode, ReactText, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Button, Modal, Table, Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { LockOutlined } from '@ant-design/icons';
import {
    CardLayout,
    Catcher,
    ChangeStatusToolBar,
    LinkComponent,
    MultiFeaturesShowTags,
    TableItemToolbar,
    cardLayoutOptions,
    columnToolbar,
    notSelectItem,
} from '../../';
import { ShowSubscriberStatus } from './ShowSubscriberStatus';
import { appConfig, isCompanyTypeIdIsRight } from '../../../settings';
import {
    ICardSubscriber,
    IChangeStatusCommands,
    IColumnOption,
    IDataTypeMain,
    IGetApiType,
    IRowSelectionCheckboxProps,
} from '../../../interfaces';
import { useCardInfo, useCardSubscribers, useProfile, useProfileFeatureFlags, useProfileIsRight } from '../../../hooks';
import {
    deleteSubscribers,
    getCard,
    getCardSubscribersStatusBackground,
    isCardSubscribersDispatchLock,
    setCardSubscribersChangeStatus,
} from '../../../middleware';
import { cleanColumnsDataSource } from '../../../instruments';
import { tB, tF, tI } from '../../../translate';
import '../../../customStyle/table.less';
import ViewStyles from '../../styles/m_viewStyles.less';

type ISubscribers = {
    columnOption?: IColumnOption;
};

type IDataType = IDataTypeMain &
    ICardSubscriber & {
        showStatus?: ReactNode;
        showLocked?: ReactNode;
        operatorLink?: ReactNode;
        imsiLink?: ReactNode;
        zoneLink?: ReactNode;
        featuresTags?: ReactNode;
    };

export const Subscribers: React.FC<ISubscribers> = observer(
    ({ columnOption = cardLayoutOptions.cardTableSubscribers }: ISubscribers) => {
        const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
        const isSubscribersFeatures = useProfileFeatureFlags()?.subscribersFeatures;
        const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);
        const subscribers = useCardSubscribers();
        const { subtreeIds } = useProfile();
        const isCompanyRight = isCompanyTypeIdIsRight('1');
        const isLord = useProfileIsRight('LORD');
        const isRoot = useProfileIsRight('ROOT');
        const { isCompanyTypeIdMNOHost } = useProfile();
        const isCustomerCare = useProfileIsRight('CUSTOMER_CARE');

        const componentSpinners: IGetApiType[] = [
            'GET_CARD',
            'DELETE_CARD_SUBSCRIBERS',
            'DELETE_CARD_WITH_SUBSCRIBERS',
        ];
        const card = useCardInfo();

        const checkLock = (id?: number): boolean => {
            return !!id && !!subtreeIds && !subtreeIds.includes(id);
        };

        const onSelectChange = (selectItems: ReactText[]): void => {
            const dispatchLockSubscribers = subscribers
                .filter((s: ICardSubscriber) => s.id && s.dispatchLock)
                .map((s: ICardSubscriber) => s.id);
            const select = selectItems.map((i) => i.toString()).filter((i) => !dispatchLockSubscribers.includes(i));
            setSelectedRowKeys(select);
        };

        const rowSelection = {
            selectedRowKeys,
            onChange: onSelectChange,
            getCheckboxProps: (record: ICardSubscriber): IRowSelectionCheckboxProps => ({
                disabled: checkLock(record.suspenderCompanyId) && !isLord,
            }),
        };

        const changeStatus = (command: IChangeStatusCommands): void => {
            setCardSubscribersChangeStatus({ command, idList: selectedRowKeys }).then();
            setSelectedRowKeys([]);
        };

        const handleDeleteClick = (): void => {
            setIsDeleteModalVisible(true);
        };

        const handleDeleteConfirm = async (): Promise<void> => {
            try {
                const { isError } = await deleteSubscribers(card.id, selectedRowKeys);
                setSelectedRowKeys([]);
                setIsDeleteModalVisible(false);

                if (!isError) {
                    await getCard(card.id);
                }
            } catch (error) {
                console.error('Failed to delete subscribers:', error);
            }
        };

        const handleDeleteCancel = (): void => {
            setIsDeleteModalVisible(false);
        };

        useEffect(() => {
            const interval = setInterval(() => {
                if (isCardSubscribersDispatchLock()) {
                    getCardSubscribersStatusBackground(true).then();
                }
            }, appConfig.updateViewInterval);
            return (): void => clearInterval(interval);
        }, []);

        useEffect(() => {
            if (subscribers.length) {
                const allSubscribers = subscribers.map((s) => s.id);
                setCardSubscribersChangeStatus({ command: 'refresh', idList: allSubscribers }).then();
            }
        }, [subscribers.length]);

        const tableColumns: ColumnsType<IDataType> = [
            isLord
                ? {}
                : {
                      dataIndex: 'showLocked',
                      className: ViewStyles.columnCenter,
                  },
            {
                title: tF('Status'),
                dataIndex: 'showStatus',
                className: ViewStyles.columnCenter,
            },
            {
                title: tF('Operator'),
                dataIndex: 'operatorLink',
            },
            {
                title: tF('IMSI'),
                dataIndex: 'imsiLink',
            },
            {
                title: tF('Roaming'),
                dataIndex: 'roamingImsi',
            },
            {
                title: tF('ICCID'),
                dataIndex: 'iccId',
            },
            {
                title: tF('MSISDN'),
                dataIndex: 'msisdn',
            },
            {
                title: tF('Zone'),
                dataIndex: 'zoneLink',
            },
            isSubscribersFeatures
                ? {
                      title: tF('Features'),
                      dataIndex: 'featuresTags',
                  }
                : {},
            isLord ? columnToolbar : {},
        ];

        const tableData: IDataType[] = subscribers.map((s: ICardSubscriber) => {
            const isLock = checkLock(s.suspenderCompanyId);

            return {
                key: s.id,
                id: s.id,
                showStatus: (
                    <ShowSubscriberStatus
                        dispatchLock={s.dispatchLock}
                        subStatus={s.statusNew}
                        transactionError={isRoot ? s.transactionError : undefined}
                    />
                ),
                showLocked: isLock ? (
                    <Tooltip title={tI('The update is locked')} placement="top">
                        <LockOutlined className={ViewStyles.redIcon} />
                    </Tooltip>
                ) : null,
                operatorLink: (
                    <LinkComponent currentListComponent={'OperatorsList'} id={s.operatorId} name={s.operator} />
                ),
                imsiLink: <LinkComponent currentListComponent={'SubscribersList'} id={s.id} name={s.imsi} />,
                roamingImsi: s.roamingImsi,
                iccId: s.iccId,
                msisdn: s.msisdn,
                zoneLink: <LinkComponent currentListComponent={'ZonesList'} id={s.zoneId} name={s.zone} />,
                featuresTags: isSubscribersFeatures ? <MultiFeaturesShowTags values={s.features} /> : null,
                toolbar: (
                    <TableItemToolbar
                        id={s.id}
                        itemName={`"${s.imsi}" ${tI('subscriber')}`}
                        currentListComponent={'SubscribersList'}
                    />
                ),
            };
        });

        return (
            <Catcher>
                <CardLayout
                    columnOption={columnOption}
                    count={subscribers.length}
                    idSpinners={componentSpinners}
                    isTable
                    title={'Subscriber identities'}
                    titleToolBar={
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            {!isCompanyTypeIdMNOHost && isCustomerCare ? (
                                <ChangeStatusToolBar
                                    changeStatus={changeStatus}
                                    disabled={!selectedRowKeys.length}
                                    disabledHint={notSelectItem}
                                    statuses={
                                        ['refresh', isCompanyRight && isLord && 'activate', 'block', 'unblock'].filter(
                                            Boolean,
                                        ) as IChangeStatusCommands[]
                                    }
                                />
                            ) : null}
                            {isLord ? (
                                <Button danger disabled={!selectedRowKeys.length} onClick={handleDeleteClick}>
                                    {tI('Delete')}
                                </Button>
                            ) : null}
                        </div>
                    }
                >
                    <Table
                        className={'table-theme'}
                        {...cleanColumnsDataSource<IDataType>(tableColumns, tableData)}
                        pagination={false}
                        rowSelection={!isCompanyTypeIdMNOHost && isCustomerCare ? rowSelection : undefined}
                        size={'middle'}
                    />
                </CardLayout>

                <Modal
                    title={'Confirm Deletion'}
                    wrapClassName={'modal'}
                    open={isDeleteModalVisible}
                    onOk={handleDeleteConfirm}
                    onCancel={handleDeleteCancel}
                    okText={tB('Yes')}
                    cancelText={tB('No')}
                    okButtonProps={{ danger: true }}
                    width={800}
                >
                    <div>
                        <p>
                            {'Are you sure you want to delete the following subscribers? This action cannot be undone.'}
                        </p>
                        <div style={{ marginTop: '16px', marginBottom: '16px' }}>
                            <strong>
                                {'Selected subscribers'} ({selectedRowKeys.length}):
                            </strong>
                            <Table
                                className={'table-theme'}
                                columns={[
                                    {
                                        title: tF('Status'),
                                        dataIndex: 'showStatus',
                                        className: ViewStyles.columnCenter,
                                        width: 80,
                                    },
                                    {
                                        title: tF('Operator'),
                                        dataIndex: 'operator',
                                        width: 120,
                                    },
                                    {
                                        title: tF('IMSI'),
                                        dataIndex: 'imsi',
                                        width: 150,
                                    },
                                    {
                                        title: tF('ICCID'),
                                        dataIndex: 'iccId',
                                        width: 120,
                                    },
                                    {
                                        title: tF('MSISDN'),
                                        dataIndex: 'msisdn',
                                        width: 120,
                                    },
                                    {
                                        title: tF('Zone'),
                                        dataIndex: 'zone',
                                    },
                                ]}
                                dataSource={subscribers
                                    .filter((s: ICardSubscriber) => selectedRowKeys.includes(s.id))
                                    .map((s: ICardSubscriber) => ({
                                        key: s.id,
                                        showStatus: (
                                            <ShowSubscriberStatus
                                                dispatchLock={s.dispatchLock}
                                                subStatus={s.statusNew}
                                                transactionError={isRoot ? s.transactionError : undefined}
                                            />
                                        ),
                                        operator: s.operator,
                                        imsi: s.imsi,
                                        iccId: s.iccId,
                                        msisdn: s.msisdn,
                                        zone: s.zone,
                                    }))}
                                pagination={false}
                                size={'small'}
                                style={{ marginTop: '8px' }}
                            />
                        </div>
                    </div>
                </Modal>
            </Catcher>
        );
    },
);
